/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import html2canvas from "html2canvas";

export type AssistantPage="dashboard"|"travel"|"planner"|"english"|"french"|"ielts"|"delf"|"news"|"settings"|"unknown";
export type AssistantPageContextValue={
  page:AssistantPage;
  route:string;
  title:string;
  view?:string;
  entity?:{type:string;id:string;label:string};
  selection?:Record<string,string|number|boolean|null>;
  draft?:Record<string,string|number|boolean|null|string[]>;
  instanceId:string;
  applyDraft?:(patch:Record<string,unknown>)=>boolean;
};

type PageContextApi={context:AssistantPageContextValue;setPageContext:(value:AssistantPageContextValue)=>void;applyClientAction:(instanceId:string,patch:Record<string,unknown>)=>boolean};
const fallback:AssistantPageContextValue={page:"unknown",route:"/",title:"当前页面",instanceId:"app"};
const Context=createContext<PageContextApi>({context:fallback,setPageContext:()=>undefined,applyClientAction:()=>false});

export function AssistantPageContextProvider({initial,children}:{initial:AssistantPageContextValue;children:ReactNode}){
  const[context,setContext]=useState(initial);const applyDraftRef=useRef(initial.applyDraft);
  const setPageContext=useCallback((next:AssistantPageContextValue)=>{applyDraftRef.current=next.applyDraft;const{applyDraft:ignored,...safeNext}=next;void ignored;setContext(current=>{const{applyDraft:currentIgnored,...safeCurrent}=current;void currentIgnored;return JSON.stringify(safeCurrent)===JSON.stringify(safeNext)?current:safeNext})},[]);
  const value=useMemo<PageContextApi>(()=>({context,setPageContext,applyClientAction:(instanceId,patch)=>context.instanceId===instanceId&&Boolean(applyDraftRef.current?.(patch))}),[context,setPageContext]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useAssistantPageContext(){return useContext(Context)}

export async function captureCurrentPage():Promise<{id:string;name:string;mimeType:string;size:number;dataUrl:string}>{
  const target=document.querySelector<HTMLElement>(".app-shell");if(!target)throw new Error("暂时无法读取当前页面");
  const canvas=await html2canvas(target,{backgroundColor:"#fffaf8",scale:Math.min(1.25,window.devicePixelRatio||1),useCORS:true,logging:false,width:window.innerWidth,height:window.innerHeight,windowWidth:window.innerWidth,windowHeight:window.innerHeight,ignoreElements:element=>element.classList.contains("assistant-layer")||element.classList.contains("assistant-fab")||element.classList.contains("assistant-history-layer")||element.hasAttribute("data-ai-sensitive")});
  let quality=.78;let dataUrl=canvas.toDataURL("image/jpeg",quality);while(dataUrl.length>3_600_000&&quality>.45){quality-=.1;dataUrl=canvas.toDataURL("image/jpeg",quality)}
  const size=Math.round((dataUrl.length-dataUrl.indexOf(",")-1)*.75);if(size>3*1024*1024)throw new Error("当前页面截图仍超过 3MB，请缩小浏览器窗口后重试");
  return{id:`page-${Date.now()}`,name:"当前页面.jpg",mimeType:"image/jpeg",size,dataUrl};
}
